import type { DevTelemetryErrorDto } from '@/api';
import { formatDateTimeDisplay } from '@/utils/date-utils';
import { Table } from '@lego/connect-components-react';
import { Text } from '@lego/klik-ui';
import type { FrontendError } from '../frontend-error-store';
import { CompactTable } from './compact-table';

export const BackendErrorsTable = ({
  errors,
}: {
  errors: DevTelemetryErrorDto[];
}) => {
  if (errors.length === 0) {
    return <Text>No failed spans recorded by the backend instance.</Text>;
  }

  return (
    <CompactTable ariaLabel="Backend errors">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Occurred At</Table.HeaderCell>
          <Table.HeaderCell>Span</Table.HeaderCell>
          <Table.HeaderCell>HTTP Status</Table.HeaderCell>
          <Table.HeaderCell>Error</Table.HeaderCell>
          <Table.HeaderCell>Trace Id</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {errors.map((error) => (
          <Table.Row key={`${error.traceId}-${error.occurredAt.getTime()}`}>
            <Table.Cell>{formatDateTimeDisplay(error.occurredAt)}</Table.Cell>
            <Table.Cell>
              {error.name} ({error.source})
            </Table.Cell>
            <Table.Cell>{error.httpStatusCode ?? '-'}</Table.Cell>
            <Table.Cell>
              {[
                error.exceptionType,
                error.exceptionMessage ?? error.description,
              ]
                .filter(Boolean)
                .join(': ') || '-'}
            </Table.Cell>
            <Table.Cell>
              <code>{error.traceId}</code>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </CompactTable>
  );
};

export const FrontendErrorsTable = ({
  errors,
}: {
  errors: readonly FrontendError[];
}) => {
  if (errors.length === 0) {
    return <Text>No errors or warnings in this browser tab.</Text>;
  }

  return (
    <CompactTable ariaLabel="Frontend errors">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Occurred At</Table.HeaderCell>
          <Table.HeaderCell>Level</Table.HeaderCell>
          <Table.HeaderCell>Source</Table.HeaderCell>
          <Table.HeaderCell>Message</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {errors.map((error, index) => (
          <Table.Row
            // Entries have no ID; the list only grows at the start, so time plus position is stable enough.
            key={`${error.occurredAt.getTime()}-${errors.length - index}`}
          >
            <Table.Cell>{formatDateTimeDisplay(error.occurredAt)}</Table.Cell>
            <Table.Cell>{error.level}</Table.Cell>
            <Table.Cell>{error.source}</Table.Cell>
            <Table.Cell>{error.message}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </CompactTable>
  );
};
